// frontend/src/components/loi/LoiPreviewPanel.tsx
/**
 * The letter, section by section (spec 3.6). ⛔ Nothing here files anything - Generate does, as before.
 * Copy uses plain hyphens only.
 */
import { useRef, useState, type ReactNode } from 'react';
import { Button } from '../ui';
import { tooLongForOneChange, type LoiOverridesState } from './useLoiOverrides';
import type { Replaced, SectionOverride } from '../../types/loiOverride';
import { changeName, HouseStyleList, sectionInWords } from './LoiHouseStyle';

/**
 * ⭐ His ruling 2026-09-30: latest wins, and the earlier change says what a later change to its section did to it - the
 *   engine's answer (the preview's outcome), in his words. Typed by the kinds, so a new one cannot arrive without words.
 */
const REPLACED_IN_WORDS: Record<Replaced['what'], string> = {
  whole: 'replaced by your later change',
  words: 'its words replaced by your later change',
  section: 'its section removed by your later change',
};

/**
 * D5: what a new section's title holds. ⛔⛔ Ruling 31: his WORDS have no maxLength - it cut a paste of his exact words
 *    past 4,000 characters without a word, into a legal letter. What one change holds is said beside them, in the box.
 */
const MAX_TITLE_CHARS = 120;

/** `**x**` -> <strong>x</strong>, everything else as text (never HTML). */
export const Bolded = ({ text }: { text: string }) => (
  <>{text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : <span key={i}>{part}</span>)}</>
);

function ChangeBox({ sectionKey, sectionTitle, state, onDone }: { sectionKey: string; sectionTitle: string; state: LoiOverridesState; onDone: () => void }) {
  const [words, setWords] = useState('');
  const [exact, setExact] = useState(false);
  const [addTitle, setAddTitle] = useState<string | null>(null);
  /**
   * ⛔⛔ NEW-1: this box's OWN draft is out. Its words, title and checkbox are locked until the answer - the box closes on
   *    a proposal, and anything typed meanwhile would go with it (probe F). The grey box also says it is working.
   */
  const [asking, setAsking] = useState(false);
  /**
   * ⛔⛔ Ruling 31: words past what one change holds are said HERE, beside them, live - and the button waits until they
   *    fit. The 111th's look found the hook's refusal only at the TOP of the panel, off screen from a box on section 11,
   *    so "Put it in" looked dead. They stay in the box to cut down, and nothing too long is ever sent.
   */
  const tooLong = tooLongForOneChange(words);
  const put = async () => {
    if (exact) {
      // His own words need no AI and are in "Your changes" at once, so the box can close now (words that do not fit
      // never get here - the button waits while `tooLong` says why).
      if (addTitle !== null) void state.exactWords(sectionKey, words, addTitle || 'Added');
      else void state.exactWords(sectionKey, words);
      onDone();
      return;
    }
    const instruction = addTitle !== null ? `Add a new section titled "${addTitle || 'Added'}" after this one: ${words}` : words;
    setAsking(true);
    let proposed = false;
    try {
      proposed = await state.draft(instruction, sectionKey, 'document');
    } catch {
      // ⛔ D8: `draft` reports its own failures and never throws - if it ever does, his words stay here to try again.
    } finally {
      // ⛔ D8: whatever the draft did, the box can never stay locked.
      setAsking(false);
    }
    // ⛔⛔ I2: closed only once the AI PROPOSED a change - on no change or a refusal his words stay here to try again.
    if (proposed) onDone();
  };
  return (
    /* ⛔⛔ The box sits INSIDE the letter's form, so Enter on its title or "Use exactly these words" would submit it -
          Generate (I1: the checkbox did). The form refuses that for every box in it (`enterNeverFiles`, the 113th). */
    <div className="mt-2 rounded border border-gray-200 bg-gray-50 p-2">
      {addTitle !== null && (
        <input className="mb-1 w-full rounded border-gray-300 text-sm" placeholder="The new section's title" maxLength={MAX_TITLE_CHARS}
          value={addTitle} disabled={asking} onChange={(e) => setAddTitle(e.target.value)} />
      )}
      <textarea className="w-full rounded border-gray-300 text-sm" rows={3} value={words} disabled={asking}
        placeholder="Tell the AI what this section should say" onChange={(e) => setWords(e.target.value)} />
      {tooLong && <p className="mt-1 text-[12px] text-amber-900">{tooLong}</p>}
      <label className="mt-1 flex items-center gap-1 text-[12px] text-gray-600">
        <input type="checkbox" checked={exact} disabled={asking} onChange={(e) => setExact(e.target.checked)} />
        Use exactly these words
      </label>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button type="button" onClick={() => void put()} disabled={!words.trim() || state.busy !== null || tooLong !== null}>{exact ? 'Put it in' : 'Draft it'}</Button>
        {addTitle === null && (
          <>
            <Button type="button" variant="outline" onClick={() => { void state.removeSection(sectionKey, sectionTitle); onDone(); }}>Remove this section</Button>
            <Button type="button" variant="outline" onClick={() => setAddTitle('')}>Add a section after</Button>
          </>
        )}
        <Button type="button" variant="ghost" onClick={onDone}>Cancel</Button>
      </div>
    </div>
  );
}

export default function LoiPreviewPanel({ state, houseStyleSlot }: { state: LoiOverridesState; houseStyleSlot?: (o: SectionOverride) => ReactNode }) {
  /**
   * ⛔ Ruling 28b: the open box is its section AND a number for this opening. A late answer closes only the box that
   *   ASKED - never a box he opened since, even on the same section (probe B: the new box and his new words went).
   */
  const [open, setOpen] = useState<{ key: string; box: number } | null>(null);
  const boxNo = useRef(0);
  const closeBox = (box: number) => () => setOpen((cur) => (cur?.box === box ? null : cur));
  const p = state.preview;
  /** The letter's own title for a section it has - or had, before a change of his removed it. */
  const onScreen = (key: string) => p?.sections.find((s) => s.key === key)?.title ?? p?.removed.find((r) => r.key === key)?.title;
  const titleOf = (key: string) => onScreen(key) ?? sectionInWords(key);
  const beforeOf = (key: string) => p?.sections.find((s) => s.key === key)?.paragraphs ?? [];
  return (
    <div className="mt-4 rounded-lg border border-gray-200 p-3" data-testid="loi-preview">
      <div className="text-sm font-medium text-gray-900">The letter, section by section</div>
      <p className="text-[12px] text-gray-500">Nothing is filed until you press Generate.</p>
      <HouseStyleList state={state} />
      {/* Ruling 26c: every edit is counted, but the notice is about a preview - with none on screen there is nothing to be stale. */}
      {state.stale && p && <p className="mt-1 text-[12px] text-amber-800">The form changed since this preview - press Preview the letter again.</p>}
      {state.error && <p className="mt-1 text-[12.5px] text-amber-900" data-testid="loi-preview-error">{state.error}</p>}
      {state.busy && <p className="mt-1 text-[12px] text-gray-500">{state.busy === 'draft' ? 'Drafting your change...' : 'Building the letter...'}</p>}

      {state.pending.map((x) => (
        <div key={x.pendingId} className="mt-3 rounded border border-violet-200 bg-violet-50 p-2" data-testid="loi-pending">
          <div className="text-[12.5px] font-medium">
            Proposed {x.change.action === 'remove' ? 'removal of' : x.change.action === 'add_after' ? `new section after` : 'change to'} {titleOf(x.change.section)}
            <span className="font-normal text-gray-500"> - you said: "{x.instruction}"</span>
          </div>
          {x.change.action === 'rewrite' && (
            <div className="mt-1 text-[12px] text-gray-500">Now: {beforeOf(x.change.section).map((t, i) => <p key={i}><Bolded text={t} /></p>)}</div>
          )}
          {x.change.action !== 'remove' && (
            <div className="mt-1 text-[12.5px]">{x.change.title && <strong>{x.change.title}: </strong>}{x.change.paragraphs.map((t, i) => <p key={i}><Bolded text={t} /></p>)}</div>
          )}
          {x.change.flags.map((f) => <p key={f} className="mt-1 text-[12px] text-amber-800">Check: {f}</p>)}
          <div className="mt-2 flex gap-2">
            <Button type="button" onClick={() => void state.accept(x.pendingId)}>Accept</Button>
            {/* ⛔ I2: the proposal he has stays until the new draft brings a change - an empty or refused redraft loses nothing.
                  ⛔ Ruling 26b: while the AI works, a second press would be a second PAID draft. */}
            <Button type="button" variant="outline" disabled={state.busy !== null}
              onClick={async () => { if (await state.draft(x.instruction, x.target, x.source)) state.discard(x.pendingId); }}>Ask again</Button>
            <Button type="button" variant="ghost" onClick={() => state.discard(x.pendingId)}>Discard</Button>
          </div>
        </div>
      ))}
      {state.refused.length > 0 && (
        <ul className="mt-2 text-[12px] text-gray-600">{state.refused.map((r) => <li key={r}>Not used: {r}</li>)}</ul>
      )}
      {/* ⛔⛔ I2: every draft that brought nothing is SAID and stays - his words quoted, the section by its title (never
            its key) or "the letter", and why: no change, or the refusal in the server's words. */}
      {state.misses.map((m) => {
        const where = m.target ? titleOf(m.target) : 'the letter';
        return (
          <p key={`${m.target ?? ''}|${m.instruction}`} className="mt-2 text-[12.5px] text-amber-900">
            {m.refusal === null
              ? `The AI made no change to ${where} for "${m.instruction}" - nothing in the letter changed.`
              : `The change to ${where} you asked for ("${m.instruction}") was not drafted: ${m.refusal}`}
          </p>
        );
      })}

      {p?.dangling.map((d) => (
        <div key={`${d.inSection}-${d.refersTo}`} className="mt-3 rounded border border-amber-300 bg-amber-50 p-2 text-[12.5px] text-amber-900">
          {d.inTitle} refers to the {d.refersToTitle} section, which this letter no longer has - Generate will refuse until that sentence changes.
          <Button type="button" variant="outline" className="ml-2" disabled={state.busy !== null} onClick={() => void state.draft(`Update this section so it no longer refers to the ${d.refersToTitle} section.`, d.inSection, 'document')}>Fix it with the AI</Button>
        </div>
      ))}
      {p?.outcomes.filter((o) => !o.applied).map((o) => {
        // ⭐ D4: WHICH change could not apply - his own (its section and his words) or his house style's - never a bare reason.
        const own = state.overrides.find((x) => x.id === o.id);
        const rule = own ? undefined : p.houseStyle.find((r) => `hs:${r.id}` === o.id);
        const which = own
          ? `${changeName(own, onScreen)} ("${own.asked}")`
          : rule ? `Your house style for ${changeName(rule, onScreen)} ("${rule.asked}")` : null;
        return <p key={o.id} className="mt-2 text-[12.5px] text-amber-900">{which ? `${which}: ${o.reason}` : o.reason}</p>;
      })}

      {p?.sections.map((s, i) => (
        <div key={s.key} className="mt-3 border-t border-gray-100 pt-2" data-testid={`loi-section-${s.key}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="text-[13px] font-semibold">{`${i + 1}. ${s.title}`}</div>
            <div className="flex items-center gap-2">
              {s.changedBy.length > 0 && <span className="rounded bg-violet-100 px-1.5 text-[11px] text-violet-900">changed</span>}
              {s.houseStyle.length > 0 && <span className="rounded bg-emerald-100 px-1.5 text-[11px] text-emerald-900">your house style</span>}
              <Button type="button" variant="ghost" onClick={() => setOpen(open?.key === s.key ? null : { key: s.key, box: ++boxNo.current })}>Change this</Button>
            </div>
          </div>
          {s.paragraphs.map((t, j) => <p key={j} className="text-[12.5px] text-gray-800"><Bolded text={t} /></p>)}
          {/* ⛔ A new box per opening (`key`), closed only by its own answer or its own buttons. */}
          {open?.key === s.key && <ChangeBox key={open.box} sectionKey={s.key} sectionTitle={s.title} state={state} onDone={closeBox(open.box)} />}
        </div>
      ))}

      {state.overrides.length > 0 && (
        <div className="mt-4 border-t border-gray-200 pt-2" data-testid="loi-your-changes">
          <div className="text-[12.5px] font-medium">Your changes ({state.overrides.length})</div>
          {state.overrides.map((o) => {
            const replaced = p?.outcomes.find((x) => x.id === o.id)?.replaced;
            /**
             * ⛔ Undo takes a change out of this list at once, and the preview answers a moment later - or never, if it
             *   fails. A mark naming a change he has since undone is that preview not having caught up: never shown. (His
             *   house style is applied first, so the change that replaced one of his is always one of his own.) And a kind
             *   this screen has no words for says nothing - never an empty mark.
             */
            const said = replaced && state.overrides.some((x) => x.id === replaced.by) ? REPLACED_IN_WORDS[replaced.what] : undefined;
            return (
              <div key={o.id} data-change={o.id} className="mt-1 text-[12px]">
                <div className="flex flex-wrap items-center gap-2">
                  {/* The mark comes FIRST - his quoted words can run to 4,000 characters and must not push it out of sight - and
                      INSIDE his words, so it wraps with them: as its own item in the row the look found it alone on a line,
                      midway between two rows, reading as the row above's. */}
                  <span>
                    {said && <span className="mr-1.5 rounded bg-amber-100 px-1.5 text-[11px] text-amber-900">{said}</span>}
                    {changeName(o, onScreen)}: {o.action === 'remove' ? 'removed' : `"${o.asked}"`}
                  </span>
                  <Button type="button" variant="ghost" onClick={() => void state.undo(o.id)}>Undo</Button>
                  {houseStyleSlot?.(o)}
                </div>
                {/* ⭐ Ruling 26a: what the fact check flagged stays beside the change after he accepts it (spec 3.2/3.5). */}
                {o.flags.map((f) => <p key={f} className="text-[12px] text-amber-800">Check: {f}</p>)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
